import { batch } from 'react-redux';
import { createSlice } from '@reduxjs/toolkit';
import { fromJS } from 'immutable';

import { getDailyReviewRowIds } from '../TimesheetsPage/dailyReview/selectors';
import { actions as dailyReviewActions } from '../TimesheetsPage/dailyReview/slice';
import { updateDailyReviewRow } from '../TimesheetsPage/dailyReview/thunks';
import {
  editTimecard,
  timecardModalActions,
} from '../TimesheetsPage/TimecardModal/slices';
import {
  convertOldShiftDataToNewFormat,
  determineContext,
} from '../TimesheetsPage/TimecardModal/utils';

import { deleteRow, updateRow } from './actions/timesheets';
import { getColumnVisibility } from './selectors/timecard';

// Initial state
const INITIAL_STATE = fromJS({
  isOpen: false,
  shiftId: null,
  mainInfoDisplayed: false,
  groupRows: [],
  shiftData: null,
  editableField: null,
  forceUpdate: false,
  isChanged: false,
  changedFields: [],
  submittedFields: [],
});

// Create slice
const timecardSlice = createSlice({
  name: 'timecard',
  initialState: INITIAL_STATE,
  reducers: {
    enableEditMode: (state, action) =>
      state.merge({
        editableField: action.payload.fieldName,
        forceUpdate: !!action.payload.forceUpdate,
      }),
    submitChangesSuccess: (state, action) =>
      state.merge({
        isChanged: true,
        shiftId: action.payload.shift_id,
        changedFields: state.get('submittedFields'),
        submittedFields: [],
      }),
  },
});

// Export actions
export const { enableEditMode, submitChangesSuccess } = timecardSlice.actions;

// Thunks (async actions)
export const showTimecardModal =
  (shiftId, eventName, shiftData, timecardOptions = {}) =>
  (dispatch, getState) => {
    const state = getState();

    let shifts;

    if (!shiftId) {
      shifts = [
        {
          date: shiftData.date,
          job_id: shiftData.job_id,
          role_id: shiftData.role_id,
        },
      ];
    } else if (timecardOptions.groupRows) {
      shifts = timecardOptions.groupRows.map(shift =>
        convertOldShiftDataToNewFormat(shift, state)
      );
    } else if (timecardOptions.context === 'dailyReview') {
      const sortedRowIds = getDailyReviewRowIds(state);

      shifts = sortedRowIds
        .map(rowId =>
          state
            .getIn(['dailyReview', 'rows'])
            .find(row => row.get('id') === rowId)
        )
        .toJS();
    } else if (shiftData) {
      shifts = [shiftData];
    } else {
      shifts = [{ id: shiftId }];
    }

    dispatch(
      timecardModalActions.openTimecardModal({
        shifts,
        backboneEventName: eventName,
        currentShiftId: shiftId,
        hideTipsSection:
          !getColumnVisibility(state, 'cash_tips') &&
          !getColumnVisibility(state, 'credit_tips'),
        startingPath: timecardOptions.startingPath,
        expandBreaks: timecardOptions.expandBreaks,
        editMode: timecardOptions.editMode,
        autofillTimes: timecardOptions.autofillTimes,
        autofillBreaks: timecardOptions.autofillBreaks,
        autofillBreaksInfo: timecardOptions.autofillBreaksInfo,
        focusClockOut: timecardOptions.focusClockOut,
      })
    );

    if (timecardOptions.editMode || !shiftId) {
      dispatch(editTimecard());
    }
  };

export const fixDuplicate =
  (scheduledShiftId, shiftId, originPath) => dispatch => {
    batch(() => {
      const context = determineContext(originPath);
      if (context === 'daily_review') {
        dispatch(dailyReviewActions.deleteDailyReviewRow(shiftId));
        dispatch(updateDailyReviewRow(scheduledShiftId));
      } else {
        dispatch(updateRow(scheduledShiftId));
        dispatch(deleteRow(shiftId));
      }
    });
  };

// Selectors
export const selectTimecardState = state => state.get('timecard');
export const selectEditableField = state =>
  selectTimecardState(state)?.get('editableField');
export const selectIsChanged = state =>
  selectTimecardState(state)?.get('isChanged');

// Export reducer
export default timecardSlice.reducer;

// Legacy exports for backward compatibility
export const actionTypes = {
  SUBMIT_CHANGES_SUCCESS: submitChangesSuccess.type,
  ENABLE_EDIT_MODE: enableEditMode.type,
};
